/**
 * DTC Summary Insights Panel - Multi-Platform View
 * =================================================
 * PHASE 3 UPDATE: Now supports Brain-powered unified insights.
 * When USE_BRAIN_FOR_INSIGHTS=true, fetches from /api/brain/findings/unified
 * 
 * Shows insights for ALL DTC platforms with collapsible sections:
 * 1. Shopify Insights (collapsible)
 * 2. Amazon Insights (collapsible)
 * 3. Walmart Insights (collapsible)
 * 4. Cross-Platform Insights (collapsible) - platform comparison, product gaps, pricing
 * 
 * CRITICAL: This component now fetches from the SAME endpoint as Command Center
 * to ensure data consistency across all pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, ChevronDown, ChevronUp, Package, TrendingUp, Users, DollarSign, Target, AlertTriangle, Sparkles, X, Zap } from 'lucide-react';

// Check if Brain-powered insights are enabled
const USE_BRAIN_FOR_INSIGHTS = import.meta.env.VITE_USE_BRAIN_FOR_INSIGHTS === 'true';
import { apiClient } from '../lib/api-client';

interface Platform {
  id: string;
  platform_name: string;
  platform_type: string;
}

interface DtcInsight {
  id: string;
  platform_id?: string;
  platform_name?: string;
  date_range_start: string;
  date_range_end: string;
  insight_type: string;
  category: string;
  priority: string;
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number | null;
  confidence_score: number | null;
  source: string;
  platform?: {
    name: string;
    type: string;
  };
  related_sku?: string;
}

interface DtcSummaryInsightsPanelProps {
  platforms: Platform[];
  startDate: string;
  endDate: string;
  useBrain?: boolean; // Override to force Brain source (default: auto from env)
}

export default function DtcSummaryInsightsPanel({
  platforms,
  startDate,
  endDate,
  useBrain
}: DtcSummaryInsightsPanelProps) {
  // Determine if we should use Brain-powered insights
  const shouldUseBrain = useBrain ?? USE_BRAIN_FOR_INSIGHTS;
  const [shopifyInsights, setShopifyInsights] = useState<DtcInsight[]>([]);
  const [amazonInsights, setAmazonInsights] = useState<DtcInsight[]>([]);
  const [walmartInsights, setWalmartInsights] = useState<DtcInsight[]>([]);
  const [crossPlatformInsights, setCrossPlatformInsights] = useState<DtcInsight[]>([]);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState<DtcInsight | null>(null);
  
  // All sections collapsed by default - counters show insight count
  const [expandedSections, setExpandedSections] = useState({
    shopify: false,
    amazon: false,
    walmart: false,
    crossPlatform: false
  });

  const platformColors: Record<string, { primary: string; light: string; medium: string }> = {
    shopify: { primary: '#95BF47', light: '#F0F7E8', medium: '#C5E09B' },
    amazon_seller_central: { primary: '#FF9900', light: '#FFF3E0', medium: '#FFD699' },
    walmart: { primary: '#0071CE', light: '#E3F2FD', medium: '#90CAF9' }
  };

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'revenue_growth': return TrendingUp;
      case 'customer_retention': return Users;
      case 'profitability': return DollarSign;
      case 'platform_performance': return Target;
      case 'acquisition': return Target;
      default: return Sparkles;
    }
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchAllInsights = useCallback(async () => {
    try {
      setIsLoading(true);
      
      let allInsights: DtcInsight[] = [];
      
      // PHASE 3: Use Brain-powered unified API when enabled
      if (shouldUseBrain) {
        console.log('[DtcSummaryInsights] Using Brain-powered unified API');
        const response = await apiClient.get<{ 
          ok: boolean; 
          insights: Array<{
            id: string;
            type: string;
            category: string;
            priority: string;
            title: string;
            description: string;
            recommendation?: string;
            dollarImpact: number | null;
            confidenceScore: number | null;
            source: string;
            platformId?: string;
            platformName?: string;
            isBrainPowered: boolean;
          }>;
        }>(`/brain/findings/unified?source=dtc&limit=50`);
        
        if (response.ok && response.insights) {
          // Convert Brain format to DtcInsight format
          allInsights = response.insights.map(bi => ({
            id: bi.id,
            platform_id: bi.platformId,
            platform_name: bi.platformName,
            date_range_start: startDate,
            date_range_end: endDate,
            insight_type: bi.type,
            category: bi.category,
            priority: bi.priority,
            title: bi.title,
            description: bi.description,
            recommendation: bi.recommendation || '',
            dollar_impact: bi.dollarImpact,
            confidence_score: bi.confidenceScore,
            source: bi.isBrainPowered ? 'brain_powered' : bi.source,
            platform: bi.platformName ? { name: bi.platformName, type: bi.platformId || '' } : undefined,
          }));
        }
      } else {
        // LEGACY PATH: Use original /api/insights/unified endpoint
        const response = await apiClient.get<{ 
          insights: DtcInsight[]; 
          total: number;
          summary: any;
        }>(`/insights/unified?source_type=dtc&limit=50`);
        
        allInsights = response.insights || [];
      }
      
      // Group insights by platform (client-side) for the expandable sections
      const shopifyPlatform = platforms.find(p => p.platform_type === 'shopify');
      const amazonPlatform = platforms.find(p => p.platform_type === 'amazon_seller_central');
      const walmartPlatform = platforms.find(p => p.platform_type === 'walmart');
      
      // Filter by platform_id or platform_name
      const shopify = allInsights.filter(i => 
        i.platform_id === shopifyPlatform?.id || 
        i.platform_name?.toLowerCase().includes('shopify')
      );
      const amazon = allInsights.filter(i => 
        i.platform_id === amazonPlatform?.id || 
        i.platform_name?.toLowerCase().includes('amazon')
      );
      const walmart = allInsights.filter(i => 
        i.platform_id === walmartPlatform?.id || 
        i.platform_name?.toLowerCase().includes('walmart')
      );
      // Cross-platform: no platform_id and not matched to specific platform
      const crossPlatform = allInsights.filter(i => 
        !i.platform_id && 
        !shopify.includes(i) && 
        !amazon.includes(i) && 
        !walmart.includes(i)
      );
      
      setShopifyInsights(shopify);
      setAmazonInsights(amazon);
      setWalmartInsights(walmart);
      setCrossPlatformInsights(crossPlatform);

      console.log(`[DtcSummaryInsights] ${shouldUseBrain ? 'Brain' : 'Legacy'} - Loaded insights:`, {
        total: allInsights.length,
        shopify: shopify.length,
        amazon: amazon.length,
        walmart: walmart.length,
        crossPlatform: crossPlatform.length
      });
    } catch (error) {
      console.error('[DtcSummaryInsights] Error fetching insights:', error);
    } finally {
      setIsLoading(false);
    }
  }, [platforms, shouldUseBrain, startDate, endDate]);

  useEffect(() => {
    if (platforms.length > 0) {
      fetchAllInsights();
    }
  }, [fetchAllInsights, platforms]);

  const generateAllInsights = useCallback(async () => {
    setIsGenerating(true);
    try {
      const shopifyPlatform = platforms.find(p => p.platform_type === 'shopify');
      const amazonPlatform = platforms.find(p => p.platform_type === 'amazon_seller_central');
      const walmartPlatform = platforms.find(p => p.platform_type === 'walmart');

      // Generate insights for each platform + cross-platform
      const requests = [];
      
      if (shopifyPlatform) {
        requests.push(apiClient.post('/insights/dtc-generate', {
          startDate, endDate, platformId: shopifyPlatform.id, force: false
        }));
      }
      
      if (amazonPlatform) {
        requests.push(apiClient.post('/insights/dtc-generate', {
          startDate, endDate, platformId: amazonPlatform.id, force: false
        }));
      }
      
      if (walmartPlatform) {
        requests.push(apiClient.post('/insights/dtc-generate', {
          startDate, endDate, platformId: walmartPlatform.id, force: false
        }));
      }
      
      // Cross-platform insights
      requests.push(apiClient.post('/insights/dtc-cross-platform', {
        startDate, endDate, force: false
      }));

      await Promise.all(requests);
      await fetchAllInsights();
    } catch (error) {
      console.error('[DtcSummaryInsights] Error generating insights:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [platforms, startDate, endDate, fetchAllInsights]);

  const renderInsightCard = (insight: DtcInsight, platformColor: any) => {
    const Icon = getCategoryIcon(insight.category);
    
    return (
      <div
        key={insight.id}
        onClick={() => setSelectedInsight(insight)}
        className="bg-white rounded-lg border border-gray-200 p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
      >
        <div className="flex items-start justify-between gap-3">
          <div
            className="p-2 rounded-lg flex-shrink-0"
            style={{ backgroundColor: platformColor.light }}
          >
            <Icon className="w-5 h-5" style={{ color: platformColor.primary }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-gray-900 text-sm">{insight.title}</h4>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getPriorityStyle(insight.priority)}`}>
                {insight.priority.toUpperCase()}
              </span>
            </div>

            <p className="text-sm text-gray-700 mb-2">{insight.description}</p>

            <div className="flex items-center gap-3 text-xs flex-wrap">
              {insight.dollar_impact !== null && insight.dollar_impact > 0 && (
                <div className="flex items-center gap-1 text-green-700 font-semibold">
                  <DollarSign className="w-3 h-3" />
                  ${insight.dollar_impact.toLocaleString()} impact
                </div>
              )}
              {insight.confidence_score !== null && (
                <div className="text-gray-600">
                  {insight.confidence_score.toFixed(0)}% confidence
                </div>
              )}
            </div>
          </div>

          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </div>
      </div>
    );
  };

  const renderPlatformSection = (
    sectionKey: 'shopify' | 'amazon' | 'walmart',
    title: string,
    insights: DtcInsight[],
    color: any,
    icon: React.ElementType
  ) => {
    const Icon = icon;
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: color.light }}>
              <Icon className="w-5 h-5" style={{ color: color.primary }} />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-600">Analytics-powered recommendations</p>
            </div>
            {/* Prominent counter badge */}
            {!isLoading && insights.length > 0 && (
              <div 
                className="flex items-center gap-1 px-3 py-1.5 text-white font-bold rounded-full text-sm shadow-md"
                style={{ backgroundColor: color.primary }}
              >
                <span>{insights.length}</span>
                <span className="opacity-80 font-normal">insight{insights.length !== 1 ? 's' : ''}</span>
              </div>
            )}
            {!isLoading && insights.length === 0 && (
              <div className="px-3 py-1.5 bg-gray-100 text-gray-500 font-medium rounded-full text-sm">
                No insights yet
              </div>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {insights.length === 0 ? (
              <div className="text-center py-8">
                <Sparkles className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No insights generated yet</p>
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                {insights.map(insight => renderInsightCard(insight, color))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const shopifyPlatform = platforms.find(p => p.platform_type === 'shopify');
  const amazonPlatform = platforms.find(p => p.platform_type === 'amazon_seller_central');
  const walmartPlatform = platforms.find(p => p.platform_type === 'walmart');

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 shadow-md p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg shadow-md">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">PatternIQ Insights</h2>
                <p className="text-sm text-gray-600">Analytics-powered recommendations across all your DTC channels</p>
              </div>
            </div>
            
            <button
              onClick={generateAllInsights}
              disabled={isGenerating}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-400 transition-all shadow-md"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? 'Analyzing All Platforms...' : 'Analyze All Platforms'}
            </button>
          </div>
        </div>

        {/* Shopify Section */}
        {shopifyPlatform && renderPlatformSection(
          'shopify',
          'Shopify Insights',
          shopifyInsights,
          platformColors.shopify,
          Package
        )}

        {/* Amazon Section */}
        {amazonPlatform && renderPlatformSection(
          'amazon',
          'Amazon Insights',
          amazonInsights,
          platformColors.amazon_seller_central,
          Package
        )}

        {/* Walmart Section */}
        {walmartPlatform && renderPlatformSection(
          'walmart',
          'Walmart Insights',
          walmartInsights,
          platformColors.walmart,
          Package
        )}

        {/* Cross-Platform Insights Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 shadow-md overflow-hidden">
          <button
            onClick={() => toggleSection('crossPlatform')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-gray-900">Cross-Platform Insights</h3>
                <p className="text-xs text-gray-600">Analytics-powered cross-channel analysis</p>
              </div>
              {/* Prominent counter badge */}
              {!isLoading && crossPlatformInsights.length > 0 && (
                <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full text-sm shadow-md">
                  <span>{crossPlatformInsights.length}</span>
                  <span className="text-purple-200 font-normal">insight{crossPlatformInsights.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              {!isLoading && crossPlatformInsights.length === 0 && (
                <div className="px-3 py-1.5 bg-gray-100 text-gray-500 font-medium rounded-full text-sm">
                  No insights yet
                </div>
              )}
            </div>
            {expandedSections.crossPlatform ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.crossPlatform && (
            <div className="px-6 pb-6 border-t border-purple-200 bg-white">
              {crossPlatformInsights.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-10 h-10 mx-auto text-purple-300 mb-2" />
                  <p className="text-sm text-gray-500">No cross-platform insights yet</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Analyze All Platforms" to generate</p>
                </div>
              ) : (
                <div className="space-y-3 mt-4">
                  {crossPlatformInsights.map(insight => renderInsightCard(insight, { primary: '#9333EA', light: '#F3E8FF', medium: '#D8B4FE' }))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary Footer */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Total Insights: <strong className="text-gray-900">
                {shopifyInsights.length + amazonInsights.length + walmartInsights.length + crossPlatformInsights.length}
              </strong>
            </span>
            <span className="text-gray-500 text-xs">
              Period: {startDate} to {endDate}
            </span>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedInsight && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedInsight(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold">{selectedInsight.title}</h3>
              <button
                onClick={() => setSelectedInsight(null)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getPriorityStyle(selectedInsight.priority)}`}>
                  {selectedInsight.priority.toUpperCase()} PRIORITY
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 border border-purple-300 rounded-full text-xs font-semibold">
                  {selectedInsight.category.replace('_', ' ').toUpperCase()}
                </span>
                {selectedInsight.platform ? (
                  <span
                    className="px-3 py-1 rounded-full text-xs font-semibold border"
                    style={{
                      backgroundColor: platformColors[selectedInsight.platform.type]?.light || '#F3F4F6',
                      borderColor: platformColors[selectedInsight.platform.type]?.medium || '#D1D5DB',
                      color: platformColors[selectedInsight.platform.type]?.primary || '#6B7280'
                    }}
                  >
                    {selectedInsight.platform.name}
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border border-purple-300 rounded-full text-xs font-semibold">
                    All Platforms
                  </span>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What's Happening</h4>
                <p className="text-gray-700">{selectedInsight.description}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Recommended Action</h4>
                <p className="text-gray-700">{selectedInsight.recommendation}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {selectedInsight.dollar_impact !== null && selectedInsight.dollar_impact > 0 && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-green-700 font-semibold mb-1">POTENTIAL IMPACT</p>
                    <p className="text-2xl font-bold text-green-900">${selectedInsight.dollar_impact.toLocaleString()}</p>
                  </div>
                )}
                {selectedInsight.confidence_score !== null && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold mb-1">CONFIDENCE</p>
                    <p className="text-2xl font-bold text-blue-900">{selectedInsight.confidence_score.toFixed(0)}%</p>
                  </div>
                )}
              </div>

              {selectedInsight.related_sku && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Related To</h4>
                  <p className="text-sm text-gray-700">SKU: {selectedInsight.related_sku}</p>
                </div>
              )}

              <div className="text-xs text-gray-500">
                Analysis period: {selectedInsight.date_range_start} to {selectedInsight.date_range_end}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
